import { z } from "zod";
import { ExerciseSchema, Exercise } from "../schemas/workout";
import { generateId } from "./id";

// Browser-compatible UUID generation
function generateUUID() {
  // Use crypto.randomUUID() if available (modern browsers)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  // Fallback implementation for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Default values for different exercise types
const defaultStructures = {
  sets: {
    type: "sets" as const,
    sets: 3,
    reps: 10,
  },
  timed: {
    type: "timed" as const,
    sets: 3,
    duration: 30,
  },
  interval: {
    type: "interval" as const,
    rounds: 5,
    work: 30,
    rest: 15,
  },
  cadence: {
    type: "cadence" as const,
    sets: 3,
    reps: 10,
    tempo: "2-1-2-0",
  },
  distance: {
    type: "distance" as const,
    distance: 1000,
    unit: "m",
  },
};

/**
 * Creates a robust default exercise with all required fields for each type
 */
export function createDefaultExercise(
  type: Exercise["structure"]["type"] = "sets"
): Exercise {
  switch (type) {
    case "sets":
      return {
        id: generateId(),
        name: "Bodyweight Squat",
        type: "sets",
        rest_time: 60,
        structure: {
          type: "sets",
          sets: 3,
          reps: 12,
        },
      };
    case "timed":
      return {
        id: generateId(),
        name: "Plank",
        type: "timed",
        rest_time: 45,
        structure: {
          type: "timed",
          sets: 3,
          duration: 30,
        },
      };
    case "cadence":
      return {
        id: generateId(),
        name: "Tempo Push-Up",
        type: "cadence",
        rest_time: 60,
        structure: {
          type: "cadence",
          sets: 3,
          reps: 8,
          tempo: "3-1-3-0",
        },
      };
    default:
      // Fallback for unknown types: safe, minimal structure
      return {
        id: generateId(),
        name: "New Exercise",
        type: type,
        rest_time: 60,
        structure: {
          type: type,
          sets: 1,
          reps: 1,
        } as any,
      };
  }
}

/**
 * Gets the default structure for an exercise type
 */
function getDefaultStructure(
  type: Exercise["structure"]["type"]
): Exercise["structure"] {
  switch (type) {
    case "sets":
      return {
        type: "sets",
        sets: 5,
        reps: 5,
      };
    case "timed":
      return {
        type: "timed",
        sets: 1,
        duration: 300,
      };
    case "interval":
      return {
        type: "interval",
        rounds: 5,
        work: 30,
        rest: 15,
      };
    case "cadence":
      return {
        type: "cadence",
        sets: 3,
        reps: 10,
        tempo: "2-1-2-0",
      };
    default:
      throw new Error(`Unknown exercise type: ${type}`);
  }
}

// Validate exercise data
export function validateExercise(exerciseData: any) {
  try {
    // Ensure all required fields are present
    const validatedExercise = ExerciseSchema.parse(exerciseData);
    return { valid: true, exercise: validatedExercise, error: null };
  } catch (error) {
    console.error("Exercise validation error:", error);
    return { valid: false, exercise: null, error };
  }
}

// Safely update exercise data
export function updateExercise(
  existingExercise: Exercise,
  updates: Partial<Exercise>
) {
  // Create a merged object with the updates
  const updatedExercise = { ...existingExercise, ...updates };

  // Validate the updated exercise
  const { valid, exercise, error } = validateExercise(updatedExercise);

  if (!valid) {
    console.error("Invalid exercise update:", error);
    return existingExercise; // Return the original if validation fails
  }

  return exercise;
}

// Create a structure based on type
export function createStructureForType(type: string) {
  switch (type) {
    case "sets":
      return defaultStructures.sets;
    case "timed":
      return defaultStructures.timed;
    case "interval":
      return defaultStructures.interval;
    case "cadence":
      return defaultStructures.cadence;
    case "distance":
      return defaultStructures.distance;
    default:
      return defaultStructures.sets;
  }
}
